import { createContext, useContext, useMemo, useState, type ReactNode } from 'react'
import { dictionaries, type Locale, type Strings } from './strings'

export type { Locale } from './strings'

/*
 * Un contexte React maison plutôt qu'i18next : le dictionnaire est typé et
 * complet à la compilation, ce qui rend une bibliothèque de chargement
 * paresseux et d'interpolation inutile pour un back-office de cette taille.
 */

type I18nValue = {
  locale: Locale
  setLocale: (locale: Locale) => void
  t: Strings
  /** Choisit la bonne langue dans un champ traduit venu de la base. */
  tr: (value: Record<string, string> | null | undefined) => string
  formatMoney: (cents: number, currency?: string) => string
  formatNumber: (value: number) => string
  formatPercent: (ratio: number) => string
}

const I18nContext = createContext<I18nValue | null>(null)

const STORAGE_KEY = 'portage.locale'

function initialLocale(): Locale {
  try {
    const stored = localStorage.getItem(STORAGE_KEY)
    if (stored === 'fr' || stored === 'en') return stored
  } catch {
    // Navigation privée, stockage bloqué : le défaut suffit.
  }
  return navigator.language.startsWith('en') ? 'en' : 'fr'
}

export function I18nProvider({ children }: { children: ReactNode }) {
  const [locale, setLocaleState] = useState<Locale>(initialLocale)

  const value = useMemo<I18nValue>(() => {
    const bcp = locale === 'fr' ? 'fr-CA' : 'en-CA'

    return {
      locale,
      setLocale: (next) => {
        setLocaleState(next)
        try {
          localStorage.setItem(STORAGE_KEY, next)
        } catch {
          // Sans persistance, la langue retombe au défaut au prochain chargement.
        }
      },
      t: dictionaries[locale],

      /*
       * Un champ traduit de la base peut ne pas avoir la langue courante — un
       * lieu bilingue saisit parfois l'anglais plus tard. On retombe sur
       * l'autre langue plutôt que d'afficher un vide, parce qu'une cellule
       * vide dans une liste de créneaux est pire qu'un titre dans la mauvaise
       * langue.
       */
      tr: (field) => {
        if (!field) return ''
        return field[locale] ?? field.fr ?? field.en ?? Object.values(field)[0] ?? ''
      },

      formatMoney: (cents, currency = 'CAD') =>
        new Intl.NumberFormat(bcp, {
          style: 'currency',
          currency,
          maximumFractionDigits: cents % 100 === 0 ? 0 : 2,
        }).format(cents / 100),

      formatNumber: (n) => new Intl.NumberFormat(bcp).format(n),
      formatPercent: (ratio) =>
        new Intl.NumberFormat(bcp, { style: 'percent', maximumFractionDigits: 0 }).format(ratio),
    }
  }, [locale])

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>
}

export function useI18n(): I18nValue {
  const value = useContext(I18nContext)
  if (!value) throw new Error('useI18n hors de I18nProvider')
  return value
}
